import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { CenterApiError, createCenterApi } from "./api";
import { findIncomingOrders, orderIdSet } from "./alerting";
import { filterOperationalOrders, getOrderAttention } from "./operations";
import type {
  BootstrapData,
  CustomerVerificationCall,
  InventoryIntegration,
  MenuItem,
  Order,
  OrderStatus,
  Restaurant,
  StaffProfile,
  StaffRole,
} from "./types";

const tokenKey = "nuviway.center.session";
const soundPreferenceKey = "nuviway.center.order-sound";
function readStoredToken(): string {
  return localStorage.getItem(tokenKey) ?? sessionStorage.getItem(tokenKey) ?? "";
}
function storeToken(token: string): void {
  localStorage.setItem(tokenKey, token);
  sessionStorage.removeItem(tokenKey);
}
function clearStoredToken(): void {
  localStorage.removeItem(tokenKey);
  sessionStorage.removeItem(tokenKey);
}
function readSoundPreference(): boolean {
  return localStorage.getItem(soundPreferenceKey) === "enabled";
}
let orderAudioContext: AudioContext | null = null;
async function playIncomingOrderTone(count = 1): Promise<void> {
  orderAudioContext ??= new AudioContext();
  await orderAudioContext.resume();
  const repetitions = Math.min(Math.max(count, 1), 3);
  for (let index = 0; index < repetitions; index += 1) {
    const start = orderAudioContext.currentTime + index * 0.19;
    const oscillator = orderAudioContext.createOscillator();
    const gain = orderAudioContext.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, start);
    oscillator.frequency.exponentialRampToValueAtTime(620, start + 0.14);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.16, start + 0.018);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
    oscillator.connect(gain);
    gain.connect(orderAudioContext.destination);
    oscillator.start(start);
    oscillator.stop(start + 0.17);
  }
}
const money = (value: number) =>
  `${new Intl.NumberFormat("ar-SY").format(value)} ل.س`;
const time = (value: string) =>
  new Intl.DateTimeFormat("ar-SY", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
const shortId = (id: string) => id.slice(-7).toUpperCase();
const inventoryEndpoint =
  "https://butrufdcduurydoafqia.supabase.co/functions/v1/nuviway-api/v1/integrations/inventory/sync";
const dateTime = (value: string) =>
  new Intl.DateTimeFormat("ar-SY", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

type IconName =
  | "orders"
  | "catalog"
  | "sync"
  | "team"
  | "refresh"
  | "plus"
  | "lock"
  | "logout"
  | "user"
  | "arrow"
  | "route"
  | "bag"
  | "bike"
  | "sales"
  | "search"
  | "filter"
  | "clock"
  | "alert"
  | "close"
  | "bell"
  | "volume";

function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  const paths: Record<IconName, ReactNode> = {
    orders: <><path d="M6 3h12l2 5H4l2-5Z" /><path d="M5 8v12h14V8M8 12h8M8 16h5" /></>,
    catalog: <><rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" /><rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" /></>,
    sync: <><path d="M20 7h-5V2" /><path d="M20 7a8 8 0 0 0-14-2M4 17h5v5" /><path d="M4 17a8 8 0 0 0 14 2" /></>,
    team: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
    refresh: <><path d="M20 7h-5V2" /><path d="M20 7a8 8 0 1 0 1 7" /></>,
    plus: <><path d="M12 5v14M5 12h14" /></>,
    lock: <><rect x="4" y="10" width="16" height="11" rx="3" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" /></>,
    logout: <><path d="M10 17l5-5-5-5M15 12H3" /><path d="M15 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" /></>,
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
    route: <><circle cx="5" cy="18" r="2" /><circle cx="19" cy="6" r="2" /><path d="M7 18h3a4 4 0 0 0 4-4v-4a4 4 0 0 1 4-4" /></>,
    bag: <><path d="M6 8h12l1 13H5L6 8Z" /><path d="M9 9V6a3 3 0 0 1 6 0v3" /></>,
    bike: <><circle cx="5" cy="17" r="3" /><circle cx="19" cy="17" r="3" /><path d="M8 17h5l3-6h-5l-2-3H6M13 17l-3-6M16 7h3" /></>,
    sales: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    filter: <><path d="M4 6h16M7 12h10M10 18h4" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    alert: <><path d="M12 3 2.7 19h18.6L12 3Z" /><path d="M12 9v4M12 16.5v.1" /></>,
    close: <><path d="m6 6 12 12M18 6 6 18" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    volume: <><path d="M11 5 6 9H2v6h4l5 4V5Z" /><path d="M15 9a4 4 0 0 1 0 6M18 6a8 8 0 0 1 0 12" /></>,
  };
  return (
    <svg
      className="ui-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

const statusInfo: Record<OrderStatus, { label: string; hint: string }> = {
  pending_merchant: { label: "جديد", hint: "بانتظار المطعم" },
  preparing: { label: "قيد التجهيز", hint: "المطعم يجهز الطلب" },
  ready_for_pickup: { label: "جاهز", hint: "بانتظار السائق" },
  on_the_way: { label: "بالطريق", hint: "خرج للتسليم" },
  delivered: { label: "تم التسليم", hint: "طلب مكتمل" },
  cancelled: { label: "ملغي", hint: "لا يحتاج متابعة" },
};

const viewInfo = {
  orders: {
    eyebrow: "LIVE OPERATIONS",
    title: "حركة الطلبات",
    subtitle: "صورة مباشرة لكل طلب من القبول حتى التحصيل والتسليم.",
  },
  catalog: {
    eyebrow: "CATALOG CONTROL",
    title: "المطاعم والمنتجات",
    subtitle: "تحكّم مركزي بالظهور والأسعار والصور والمخزون.",
  },
  integrations: {
    eyebrow: "CONNECTED SYSTEMS",
    title: "ربط المخزون والمحاسبة",
    subtitle: "مزامنة ذكية تُبقي الكتالوج مطابقاً لحركة البيع.",
  },
  staff: {
    eyebrow: "FIELD NETWORK",
    title: "فريق التشغيل",
    subtitle: "إدارة السائقين والمطاعم وصلاحية الوصول من مكان واحد.",
  },
} as const;

const designPreview = import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get("preview") === "dashboard";
const alertPreview = designPreview &&
  new URLSearchParams(window.location.search).get("alert") === "1";
const previewNow = new Date().toISOString();
const previewMinutesAgo = (minutes: number) =>
  new Date(Date.now() - minutes * 60_000).toISOString();
const previewData: BootstrapData = {
  restaurants: [{
    id: "preview-kitchen",
    name: "مطبخ الياسمين",
    category: "مأكولات شرقية",
    etaMin: 25,
    etaMax: 40,
    deliveryFee: 8000,
    minimumOrder: 0,
    symbol: "ي",
    isActive: true,
    sortOrder: 0,
    updatedAt: previewNow,
  }],
  menuItems: [],
  staff: [{
    subjectId: "driver-preview",
    role: "driver",
    displayName: "أحمد السالم",
    phone: "0944000000",
    restaurantId: null,
    isActive: true,
    updatedAt: previewNow,
  }],
  inventoryIntegrations: [],
  serverTime: previewNow,
};
const previewOrders: Order[] = [
  {
    id: "order-preview-001",
    restaurantId: "preview-kitchen",
    customerId: "customer-preview",
    items: [{ itemId: "meal-1", name: "وجبة مشاوي مشكلة", unitPrice: 85000, quantity: 1, lineTotal: 85000 }],
    address: { area: "المزة", landmark: "جانب صيدلية الشام", phone: "0944000001" },
    backupPhone: "0933000001",
    total: 93000,
    status: "pending_merchant",
    prepMinutes: null,
    driverId: null,
    version: 1,
    createdAt: previewMinutesAgo(9),
    updatedAt: previewMinutesAgo(7),
  },
  {
    id: "order-preview-002",
    restaurantId: "preview-kitchen",
    customerId: "customer-preview-2",
    items: [{ itemId: "meal-2", name: "شاورما عربي", unitPrice: 42000, quantity: 2, lineTotal: 84000 }],
    address: { area: "المالكي", landmark: "قرب الحديقة", phone: "0955000002" },
    total: 92000,
    status: "preparing",
    prepMinutes: 18,
    driverId: null,
    version: 1,
    createdAt: previewMinutesAgo(22),
    updatedAt: previewMinutesAgo(17),
  },
  {
    id: "order-preview-003",
    restaurantId: "preview-kitchen",
    customerId: "customer-preview-3",
    items: [{ itemId: "meal-3", name: "كبة مقلية", unitPrice: 12000, quantity: 4, lineTotal: 48000 }],
    address: { area: "أبو رمانة", landmark: "خلف المركز", phone: "0999000003" },
    total: 56000,
    status: "ready_for_pickup",
    prepMinutes: 25,
    driverId: null,
    version: 1,
    createdAt: previewMinutesAgo(31),
    updatedAt: previewMinutesAgo(11),
  },
];

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`brand ${compact ? "compact" : ""}`} aria-label="NUVIWAY">
      <div className="brand-mark">
        <svg viewBox="0 0 96 96" aria-hidden="true">
          <path d="M25 70V28l43 43V34" />
          <path d="M57 34h11v11" />
          <circle cx="25" cy="70" r="3.5" />
        </svg>
        <i />
      </div>
      {!compact && (
        <div className="brand-copy">
          <strong>NUVIWAY</strong>
          <span>OPERATIONS</span>
        </div>
      )}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button
            className="icon-button"
            type="button"
            onClick={onClose}
            aria-label="إغلاق"
          >
            ×
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function Login({
  onLogin,
}: {
  onLogin: (username: string, password: string) => Promise<void>;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onLogin(username, password);
    } catch (value) {
      setError(value instanceof Error ? value.message : "تعذر تسجيل الدخول");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login-shell">
      <section className="login-showcase" aria-label="NUVIWAY Operations">
        <Brand />
        <div className="showcase-copy">
          <span className="showcase-kicker"><i /> مركز القيادة المباشر</span>
          <h1>
            كل العملية.
            <em> بإيقاع واحد.</em>
          </h1>
          <p>
            من لحظة وصول الطلب حتى التسليم والتحصيل—رؤية واحدة واضحة لفريق
            أسرع وقرار أدق.
          </p>
        </div>
        <div className="route-scene" aria-hidden="true">
          <div className="route-track track-one" />
          <div className="route-track track-two" />
          <span className="route-node node-order"><Icon name="bag" size={19} /></span>
          <span className="route-node node-center"><Icon name="route" size={21} /></span>
          <span className="route-node node-driver"><Icon name="bike" size={21} /></span>
          <span className="route-pulse pulse-one" />
          <span className="route-pulse pulse-two" />
          <div className="route-caption caption-order"><b>طلب جديد</b><small>تم الاستلام</small></div>
          <div className="route-caption caption-driver"><b>في الطريق</b><small>تتبّع مباشر</small></div>
        </div>
        <div className="showcase-footer">
          <span>تشغيل أسرع</span><i />
          <span>قرار أوضح</span><i />
          <span>تجربة أثقل</span>
        </div>
      </section>
      <section className="login-panel">
        <div className="login-card">
          <div className="login-card-brand"><Brand /></div>
          <div className="login-copy">
            <span className="eyebrow"><i /> بوابة فريق التشغيل</span>
            <h2>أهلاً برجعتك</h2>
            <p>ادخل إلى مركز العمليات وتابع كل شيء لحظة بلحظة.</p>
          </div>
          <form onSubmit={submit}>
          <label>
            <span className="field-label">اسم المستخدم</span>
            <span className="input-shell">
              <Icon name="user" size={19} />
              <input
                autoFocus
                autoComplete="username"
                required
                minLength={3}
                maxLength={50}
                pattern="[a-z0-9._-]+"
                placeholder="majd"
                value={username}
                onChange={(event) => setUsername(event.target.value.toLowerCase())}
              />
            </span>
          </label>
          <label>
            <span className="field-label">كلمة المرور</span>
            <span className="input-shell">
              <Icon name="lock" size={19} />
              <input
                type="password"
                autoComplete="current-password"
                required
                minLength={12}
                maxLength={128}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </span>
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            className="primary wide"
            disabled={busy || username.trim().length < 3 || password.length < 12}
          >
            <span>{busy ? "جارٍ التحقق…" : "دخول إلى مركز العمليات"}</span>
            {!busy && <Icon name="arrow" size={20} />}
          </button>
          </form>
          <div className="login-security">
            <Icon name="lock" size={15} />
            <span>اتصال مشفّر · حماية تلقائية من المحاولات المتكررة</span>
          </div>
        </div>
      </section>
    </main>
  );
}

function PasswordForm({
  onSave,
  onClose,
}: {
  onSave: (currentPassword: string, newPassword: string) => Promise<void>;
  onClose: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const currentPassword = String(data.get("currentPassword") ?? "");
    const newPassword = String(data.get("newPassword") ?? "");
    const confirmation = String(data.get("confirmation") ?? "");
    if (newPassword !== confirmation) {
      setError("تأكيد كلمة المرور غير مطابق");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave(currentPassword, newPassword);
    } catch (value) {
      setError(value instanceof Error ? value.message : "تعذر تغيير كلمة المرور");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="تغيير كلمة المرور" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label className="full">
          كلمة المرور الحالية
          <input
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
            minLength={12}
            maxLength={128}
          />
        </label>
        <label>
          كلمة المرور الجديدة
          <input
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            maxLength={128}
          />
        </label>
        <label>
          تأكيد كلمة المرور
          <input
            name="confirmation"
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            maxLength={128}
          />
        </label>
        <p className="form-hint full">
          استخدم 12 محرفاً على الأقل مع أحرف وأرقام. سيتم إلغاء أي جلسة قديمة.
        </p>
        {error && <p className="form-error full" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="ghost" onClick={onClose}>إلغاء</button>
          <button className="primary" disabled={busy}>
            {busy ? "جارٍ التغيير…" : "حفظ كلمة المرور"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function OrderCard({
  order,
  restaurant,
  drivers,
  driverName,
  now,
  isNew,
  onChanged,
}: {
  order: Order;
  restaurant?: Restaurant;
  drivers: StaffProfile[];
  driverName?: string;
  now: number;
  isNew: boolean;
  onChanged: () => Promise<void>;
}) {
  const [driverId, setDriverId] = useState(order.driverId ?? "");
  const [busy, setBusy] = useState(false);
  const api = useMemo(
    () => createCenterApi(readStoredToken),
    [],
  );
  const attention = getOrderAttention(order, now);
  const timingLabel = attention.level === "late"
    ? `تجاوز المتابعة ${attention.minutes - attention.threshold} د`
    : attention.minutes === 0
      ? "تحديث الآن"
      : `في هذه المرحلة منذ ${attention.minutes} د`;
  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      await onChanged();
    } catch (error) {
      alert(error instanceof Error ? error.message : "تعذر تنفيذ العملية");
    } finally {
      setBusy(false);
    }
  }
  return (
    <article className={`order-card attention-${attention.level}${isNew ? " just-arrived" : ""}`}>
      {isNew && <span className="new-order-ribbon">وصل الآن</span>}
      <div className="order-head">
        <span className="status-pill">{statusInfo[order.status].label}</span>
        <strong>#{shortId(order.id)}</strong>
      </div>
      <div className={`order-timing timing-${attention.level}`}>
        <Icon name={attention.level === "late" ? "alert" : "clock"} size={14} />
        <span>{timingLabel}</span>
      </div>
      <h3>{restaurant?.name ?? order.restaurantId}</h3>
      {order.customerName && <p className="customer-name">العميل: {order.customerName}</p>}
      <p className="muted">
        {order.address.area} — {order.address.landmark}
      </p>
      <div className="phone-line">
        <a href={`tel:${order.address.phone}`}>{order.address.phone}</a>
        {order.backupPhone && (
          <a href={`tel:${order.backupPhone}`}>{order.backupPhone}</a>
        )}
      </div>
      <div className="order-items">
        {order.items.map((item) => (
          <span key={item.itemId}>
            {item.quantity}× {item.name}
          </span>
        ))}
      </div>
      <div className="order-total">
        <strong>{money(order.total)}</strong>
        <span>نقداً</span>
      </div>
      <div className="order-meta">
        <span>{time(order.createdAt)}</span>
        {order.prepMinutes && <span>{order.prepMinutes} دقيقة</span>}
        {order.driverId && <span>السائق: {driverName ?? order.driverId}</span>}
      </div>
      {(["preparing", "ready_for_pickup"] as OrderStatus[]).includes(
        order.status,
      ) &&
        !order.driverId && (
          <div className="inline-action">
            <select
              value={driverId}
              onChange={(event) => setDriverId(event.target.value)}
              aria-label="اختر السائق"
            >
              <option value="">اختر السائق</option>
              {drivers.map((driver) => (
                <option key={driver.subjectId} value={driver.subjectId}>
                  {driver.displayName}
                </option>
              ))}
            </select>
            <button
              disabled={!driverId || busy}
              onClick={() => act(() => api.assignDriver(order.id, driverId))}
            >
              إسناد
            </button>
          </div>
        )}
      {order.status === "ready_for_pickup" && order.driverId && (
        <button
          className="secondary wide"
          disabled={busy}
          onClick={() => {
            const reason = prompt(
              "اكتب سبب التأكيد الهاتفي (مثال: اتصل السائق من الرقم المسجل)",
            );
            if (reason && reason.trim().length >= 10)
              void act(() =>
                api.confirmPickup(order.id, order.driverId!, reason),
              );
          }}
        >
          تأكيد الاستلام هاتفياً
        </button>
      )}
      {order.status === "on_the_way" && order.driverId && (
        <button
          className="secondary wide"
          disabled={busy}
          onClick={() => {
            const code = prompt("رمز التسليم المكوّن من 4 أرقام");
            if (!code) return;
            const reason = prompt(
              "سبب التأكيد الهاتفي وتأكيد استلام المبلغ النقدي",
            );
            if (
              reason &&
              reason.trim().length >= 10 &&
              confirm("هل أكد السائق استلام كامل المبلغ النقدي؟")
            )
              void act(() =>
                api.confirmDelivery(order.id, order.driverId!, code, reason),
              );
          }}
        >
          تأكيد التسليم هاتفياً
        </button>
      )}
    </article>
  );
}

function VerificationQueue({
  requests,
  onChanged,
}: {
  requests: CustomerVerificationCall[];
  onChanged: () => Promise<void>;
}) {
  const api = useMemo(() => createCenterApi(readStoredToken), []);
  const [codes, setCodes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (requests.length === 0) return null;

  async function issueCode(request: CustomerVerificationCall) {
    setBusyId(request.challengeId);
    setError(null);
    try {
      const issued = await api.issuePhoneVerificationCode(request.challengeId);
      setCodes((current) => ({ ...current, [request.challengeId]: issued.code }));
      await onChanged();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "تعذر إصدار رمز الاتصال");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="verification-queue" aria-label="طلبات تأكيد أرقام العملاء">
      <div className="verification-heading">
        <div>
          <span className="eyebrow"><i /> CUSTOMER VERIFICATION</span>
          <h2>اتصالات تأكيد بانتظار المركز</h2>
          <p>اتصل بالرقم، ثم اقرأ للعميل الرمز الظاهر أمامك فقط.</p>
        </div>
        <strong>{requests.length}</strong>
      </div>
      {error && <p className="verification-error" role="alert">{error}</p>}
      <div className="verification-grid">
        {requests.map((request) => {
          const code = codes[request.challengeId];
          return (
            <article className="verification-card" key={request.challengeId}>
              <div className="verification-card-copy">
                <span>{request.purpose === "primary" ? "الرقم الأساسي" : "الرقم الاحتياطي"}</span>
                <h3>{request.customerName ?? "عميل جديد"}</h3>
                <a href={`tel:${request.phone}`}>{request.phone}</a>
                <small>وصل الطلب {time(request.createdAt)} • ينتهي {time(request.expiresAt)}</small>
              </div>
              {code ? (
                <div className="verification-code" aria-live="polite">
                  <span>اقرأ هذا الرمز للعميل</span>
                  <strong>{code}</strong>
                  <small>صالح 5 دقائق ولمرة واحدة</small>
                </div>
              ) : (
                <button
                  className="primary"
                  type="button"
                  disabled={busyId === request.challengeId}
                  onClick={() => void issueCode(request)}
                >
                  <Icon name="user" size={17} />
                  {busyId === request.challengeId ? "جارٍ إصدار الرمز…" : "بدء الاتصال وإظهار الرمز"}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RestaurantForm({
  value,
  onSave,
  onClose,
}: {
  value?: Restaurant;
  onSave: (value: Omit<Restaurant, "updatedAt">) => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      await onSave({
        id: String(data.get("id")),
        name: String(data.get("name")),
        category: String(data.get("category")),
        etaMin: Number(data.get("etaMin")),
        etaMax: Number(data.get("etaMax")),
        deliveryFee: Number(data.get("deliveryFee")),
        minimumOrder: Number(data.get("minimumOrder")),
        symbol: String(data.get("symbol")),
        isActive: data.get("isActive") === "on",
        sortOrder: Number(data.get("sortOrder")),
      });
      onClose();
    } catch (error) {
      alert(error instanceof Error ? error.message : "تعذر الحفظ");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={value ? "تعديل المطعم" : "إضافة مطعم"} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label>
          المعرّف الإنكليزي
          <input
            name="id"
            required
            minLength={3}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            defaultValue={value?.id}
            readOnly={Boolean(value)}
            placeholder="damascus-grill"
          />
        </label>
        <label>
          اسم المطعم
          <input
            name="name"
            required
            minLength={2}
            defaultValue={value?.name}
          />
        </label>
        <label>
          التصنيف
          <input
            name="category"
            required
            defaultValue={value?.category ?? "مطاعم"}
          />
        </label>
        <label>
          رمز مختصر
          <input
            name="symbol"
            required
            maxLength={8}
            defaultValue={value?.symbol ?? "ن"}
          />
        </label>
        <label>
          أقل وقت بالدقائق
          <input
            name="etaMin"
            type="number"
            min={1}
            max={240}
            defaultValue={value?.etaMin ?? 25}
            required
          />
        </label>
        <label>
          أعلى وقت بالدقائق
          <input
            name="etaMax"
            type="number"
            min={1}
            max={240}
            defaultValue={value?.etaMax ?? 40}
            required
          />
        </label>
        <label>
          أجرة التوصيل
          <input
            name="deliveryFee"
            type="number"
            min={0}
            defaultValue={value?.deliveryFee ?? 0}
            required
          />
        </label>
        <label>
          الحد الأدنى
          <input
            name="minimumOrder"
            type="number"
            min={0}
            defaultValue={value?.minimumOrder ?? 0}
            required
          />
        </label>
        <label>
          ترتيب الظهور
          <input
            name="sortOrder"
            type="number"
            defaultValue={value?.sortOrder ?? 0}
            required
          />
        </label>
        <label className="check">
          <input
            name="isActive"
            type="checkbox"
            defaultChecked={value?.isActive ?? false}
          />{" "}
          ظاهر للزبائن
        </label>
        <div className="form-actions">
          <button type="button" className="ghost" onClick={onClose}>
            إلغاء
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "جارٍ الحفظ…" : "حفظ المطعم"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ItemForm({
  restaurants,
  value,
  initialRestaurantId,
  onSave,
  onClose,
}: {
  restaurants: Restaurant[];
  value?: MenuItem;
  initialRestaurantId?: string;
  onSave: (
    value: Pick<
      MenuItem,
      | "restaurantId"
      | "id"
      | "name"
      | "description"
      | "price"
      | "imageUrl"
      | "externalSku"
      | "isAvailable"
      | "sortOrder"
    >,
    image?: File,
  ) => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState<File>();
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      await onSave(
        {
          restaurantId: String(data.get("restaurantId")),
          id: String(data.get("id")),
          name: String(data.get("name")),
          description: String(data.get("description") ?? ""),
          price: Number(data.get("price")),
          imageUrl: value?.imageUrl ?? null,
          externalSku: String(data.get("externalSku") ?? "").trim() || null,
          isAvailable: data.get("isAvailable") === "on",
          sortOrder: Number(data.get("sortOrder")),
        },
        image,
      );
      onClose();
    } catch (error) {
      alert(error instanceof Error ? error.message : "تعذر الحفظ");
    } finally {
      setBusy(false);
    }
  }
  const preview = image ? URL.createObjectURL(image) : value?.imageUrl;
  return (
    <Modal title={value ? "تعديل المنتج" : "إضافة منتج"} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label>
          المطعم
          <select
            name="restaurantId"
            required
            defaultValue={value?.restaurantId ?? initialRestaurantId ?? ""}
            disabled={Boolean(value)}
          >
            <option value="">اختر المطعم</option>
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
          {value && (
            <input type="hidden" name="restaurantId" value={value.restaurantId} />
          )}
        </label>
        <label>
          معرّف المنتج
          <input
            name="id"
            required
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            defaultValue={value?.id}
            readOnly={Boolean(value)}
            placeholder="chicken-shawarma"
          />
        </label>
        <label>
          رمز المادة في المحاسبة
          <input
            name="externalSku"
            maxLength={100}
            defaultValue={value?.externalSku ?? ""}
            placeholder="مثال: 10025 أو BARCODE"
          />
        </label>
        <label>
          اسم المنتج
          <input
            name="name"
            required
            minLength={2}
            defaultValue={value?.name}
          />
        </label>
        <label>
          السعر بالليرة
          <input
            name="price"
            type="number"
            min={0}
            max={3000000}
            defaultValue={value?.price ?? 0}
            required
          />
        </label>
        <label className="full">
          الوصف
          <textarea
            name="description"
            maxLength={500}
            defaultValue={value?.description}
          />
        </label>
        <label>
          ترتيب الظهور
          <input
            name="sortOrder"
            type="number"
            defaultValue={value?.sortOrder ?? 0}
            required
          />
        </label>
        <label className="check">
          <input
            name="isAvailable"
            type="checkbox"
            defaultChecked={value?.catalogEnabled ?? value?.isAvailable ?? true}
          />{" "}
          ظاهر في الكتالوج عند توفر المخزون
        </label>
        <label className="full upload-box">
          صورة المنتج PNG/JPG/WebP — حد أقصى 5MB
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => setImage(event.target.files?.[0])}
          />
          {preview && <img src={preview} alt="معاينة المنتج" />}
          {!preview && (
            <span>يفضّل PNG بخلفية شفافة ليظهر المنتج على الأبيض</span>
          )}
        </label>
        <div className="form-actions">
          <button type="button" className="ghost" onClick={onClose}>
            إلغاء
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "جارٍ الرفع والحفظ…" : "حفظ المنتج"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function StaffForm({
  restaurants,
  onSave,
  onClose,
}: {
  restaurants: Restaurant[];
  onSave: (value: {
    role: StaffRole;
    subjectId: string;
    displayName: string;
    phone: string;
    restaurantId: string | null;
    validForHours: number;
  }) => Promise<string>;
  onClose: () => void;
}) {
  const [role, setRole] = useState<Exclude<StaffRole, "dispatcher">>("driver");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      setCode(
        await onSave({
          role,
          subjectId: String(data.get("subjectId")),
          displayName: String(data.get("displayName")),
          phone: String(data.get("phone") ?? ""),
          restaurantId:
            role === "merchant" ? String(data.get("restaurantId")) : null,
          validForHours: 24,
        }),
      );
    } catch (error) {
      alert(error instanceof Error ? error.message : "تعذر إصدار الرمز");
    } finally {
      setBusy(false);
    }
  }
  if (code)
    return (
      <Modal title="تم إنشاء رمز التفعيل" onClose={onClose}>
        <div className="activation-result">
          <p>أعطِ هذا الرمز لصاحب الحساب مرة واحدة فقط:</p>
          <strong>{code.match(/.{1,4}/g)?.join("-")}</strong>
          <button
            className="primary"
            onClick={() => void navigator.clipboard.writeText(code)}
          >
            نسخ الرمز
          </button>
          <small>ينتهي خلال 24 ساعة ولا يمكن عرضه مرة ثانية.</small>
        </div>
      </Modal>
    );
  return (
    <Modal title="إضافة حساب تشغيل" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label>
          نوع الحساب
          <select
            value={role}
            onChange={(event) =>
              setRole(event.target.value as Exclude<StaffRole, "dispatcher">)
            }
          >
            <option value="driver">سائق</option>
            <option value="merchant">مطعم / سنتر</option>
          </select>
        </label>
        <label>
          المعرّف الإنكليزي
          <input
            name="subjectId"
            required
            minLength={3}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            placeholder="driver-001"
          />
        </label>
        <label>
          الاسم الظاهر
          <input name="displayName" required minLength={2} />
        </label>
        <label>
          رقم الهاتف
          <input
            name="phone"
            inputMode="tel"
            pattern="09[0-9]{8}"
            placeholder="09xxxxxxxx"
          />
        </label>
        {role === "merchant" && (
          <label className="full">
            المطعم
            <select name="restaurantId" required>
              <option value="">اختر المطعم</option>
              {restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="form-actions">
          <button type="button" className="ghost" onClick={onClose}>
            إلغاء
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "جارٍ الإنشاء…" : "إنشاء رمز التفعيل"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InventoryIntegrationForm({
  restaurants,
  onSave,
  onClose,
}: {
  restaurants: Restaurant[];
  onSave: (
    restaurantId: string,
    name: string,
  ) => Promise<{ integration: InventoryIntegration; token: string }>;
  onClose: () => void;
}) {
  const [created, setCreated] = useState<{
    integration: InventoryIntegration;
    token: string;
  }>();
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      setCreated(
        await onSave(
          String(data.get("restaurantId")),
          String(data.get("name")),
        ),
      );
    } catch (error) {
      alert(error instanceof Error ? error.message : "تعذر إنشاء الربط");
    } finally {
      setBusy(false);
    }
  }
  if (created) {
    return (
      <Modal title="تم إنشاء مفتاح الربط" onClose={onClose}>
        <div className="integration-secret">
          <p>
            انسخ المفتاح الآن وضعه في موصل برنامج المحاسبة. لن نعرضه مرة
            ثانية.
          </p>
          <code dir="ltr">{created.token}</code>
          <button
            className="primary"
            onClick={() => void navigator.clipboard.writeText(created.token)}
          >
            نسخ المفتاح
          </button>
          <small>إذا ضاع المفتاح أوقف هذا الربط وأنشئ مفتاحاً جديداً.</small>
        </div>
      </Modal>
    );
  }
  return (
    <Modal title="ربط برنامج محاسبة" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label className="full">
          المطعم أو السنتر
          <select name="restaurantId" required defaultValue="">
            <option value="">اختر الجهة</option>
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </label>
        <label className="full">
          اسم الربط
          <input
            name="name"
            required
            minLength={2}
            maxLength={100}
            defaultValue="برنامج المحاسبة الرئيسي"
          />
        </label>
        <div className="form-actions">
          <button type="button" className="ghost" onClick={onClose}>
            إلغاء
          </button>
          <button className="primary" disabled={busy}>
            {busy ? "جارٍ الإنشاء…" : "إنشاء مفتاح الربط"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function App() {
  const [token, setToken] = useState(() => designPreview ? "design-preview" : readStoredToken());
  const api = useMemo(() => createCenterApi(readStoredToken), []);
  const [data, setData] = useState<BootstrapData>(designPreview ? previewData : {
    restaurants: [],
    menuItems: [],
    staff: [],
    inventoryIntegrations: [],
    serverTime: "",
  });
  const [orders, setOrders] = useState<Order[]>(designPreview ? previewOrders : []);
  const [phoneVerifications, setPhoneVerifications] = useState<CustomerVerificationCall[]>([]);
  const [view, setView] = useState<
    "orders" | "catalog" | "integrations" | "staff"
  >("orders");
  const [loading, setLoading] = useState(Boolean(token) && !designPreview);
  const [notice, setNotice] = useState("");
  const [restaurantModal, setRestaurantModal] = useState<
    Restaurant | "new" | null
  >(null);
  const [itemModal, setItemModal] = useState<MenuItem | "new" | null>(null);
  const [itemRestaurant, setItemRestaurant] = useState<string>();
  const [staffModal, setStaffModal] = useState(false);
  const [integrationModal, setIntegrationModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [orderQuery, setOrderQuery] = useState("");
  const [orderRestaurant, setOrderRestaurant] = useState("");
  const [orderDriver, setOrderDriver] = useState("");
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [lastOrdersRefresh, setLastOrdersRefresh] = useState(() => Date.now());
  const [soundEnabled, setSoundEnabled] = useState(readSoundPreference);
  const [incomingAlert, setIncomingAlert] = useState<{ count: number; ids: string[] } | null>(
    alertPreview ? { count: 1, ids: [previewOrders[0].id] } : null,
  );
  const [recentOrderIds, setRecentOrderIds] = useState<string[]>(
    alertPreview ? [previewOrders[0].id] : [],
  );
  const [unreadNewOrders, setUnreadNewOrders] = useState(0);
  const orderSearchRef = useRef<HTMLInputElement>(null);
  const knownOrderIds = useRef<Set<string>>(new Set());
  const hasOrderBaseline = useRef(false);
  const soundEnabledRef = useRef(soundEnabled);

  const signalIncomingOrders = useCallback((incoming: Order[]) => {
    if (incoming.length === 0) return;
    const ids = incoming.map((order) => order.id);
    setIncomingAlert((current) => ({
      count: (current?.count ?? 0) + incoming.length,
      ids: [...new Set([...(current?.ids ?? []), ...ids])],
    }));
    setRecentOrderIds((current) => [...new Set([...current, ...ids])]);
    if (document.visibilityState !== "visible") {
      setUnreadNewOrders((current) => current + incoming.length);
    }
    if (soundEnabledRef.current) void playIncomingOrderTone(incoming.length).catch(() => undefined);
  }, []);

  const applyOrderSnapshot = useCallback((nextOrders: Order[], alertChanges: boolean) => {
    if (alertChanges && hasOrderBaseline.current) {
      signalIncomingOrders(findIncomingOrders(knownOrderIds.current, nextOrders));
    }
    knownOrderIds.current = orderIdSet(nextOrders);
    hasOrderBaseline.current = true;
    setOrders(nextOrders);
  }, [signalIncomingOrders]);

  const logout = useCallback(() => {
    void api.logout().catch(() => undefined);
    clearStoredToken();
    hasOrderBaseline.current = false;
    knownOrderIds.current = new Set();
    setIncomingAlert(null);
    setRecentOrderIds([]);
    setUnreadNewOrders(0);
    setPhoneVerifications([]);
    setToken("");
  }, [api]);
  const refreshOrders = useCallback(async () => {
    if (designPreview) return;
    try {
      const [currentOrders, currentVerifications] = await Promise.all([
        api.orders(),
        api.phoneVerifications(),
      ]);
      applyOrderSnapshot(currentOrders, true);
      setPhoneVerifications(currentVerifications);
      setLastOrdersRefresh(Date.now());
    } catch (error) {
      if (error instanceof CenterApiError && error.status === 401) logout();
      else throw error;
    }
  }, [api, applyOrderSnapshot, logout]);
  const refreshAll = useCallback(async () => {
    if (designPreview) return;
    const [bootstrap, currentOrders, currentVerifications] = await Promise.all([
      api.bootstrap(),
      api.orders(),
      api.phoneVerifications(),
    ]);
    setData(bootstrap);
    applyOrderSnapshot(currentOrders, false);
    setPhoneVerifications(currentVerifications);
    setLastOrdersRefresh(Date.now());
  }, [api, applyOrderSnapshot]);

  useEffect(() => {
    if (designPreview) return;
    if (!token) return;
    setLoading(true);
    void api
      .session()
      .then(({ session }) => {
        if (session.role !== "dispatcher")
          throw new Error("هذا الحساب ليس حساب مركز خدمة");
        return refreshAll();
      })
      .catch((error) => {
        setNotice(error instanceof Error ? error.message : "تعذر تحميل اللوحة");
        logout();
      })
      .finally(() => setLoading(false));
  }, [api, logout, refreshAll, token]);
  useEffect(() => {
    if (!token || designPreview) return;
    const timer = window.setInterval(
      () => void refreshOrders().catch(() => undefined),
      5000,
    );
    return () => clearInterval(timer);
  }, [refreshOrders, token]);
  useEffect(() => {
    if (!token) return;
    const timer = window.setInterval(() => setClockNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [token]);
  useEffect(() => {
    if (!token || view !== "orders") return;
    const focusSearch = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping = target?.matches("input, textarea, select, [contenteditable='true']");
      if (event.key === "/" && !isTyping) {
        event.preventDefault();
        orderSearchRef.current?.focus();
      }
      if (event.key === "Escape" && document.activeElement === orderSearchRef.current) {
        setOrderQuery("");
        orderSearchRef.current?.blur();
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, [token, view]);
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    localStorage.setItem(soundPreferenceKey, soundEnabled ? "enabled" : "disabled");
    if (!soundEnabled) return;
    const primeAudio = () => {
      orderAudioContext ??= new AudioContext();
      void orderAudioContext.resume().catch(() => undefined);
    };
    window.addEventListener("pointerdown", primeAudio, { once: true });
    return () => window.removeEventListener("pointerdown", primeAudio);
  }, [soundEnabled]);
  useEffect(() => {
    document.title = unreadNewOrders > 0
      ? `(${unreadNewOrders}) طلب جديد | NUVIWAY`
      : "مركز خدمة NUVIWAY";
    return () => { document.title = "مركز خدمة NUVIWAY"; };
  }, [unreadNewOrders]);
  useEffect(() => {
    const markVisible = () => {
      if (document.visibilityState === "visible") setUnreadNewOrders(0);
    };
    document.addEventListener("visibilitychange", markVisible);
    return () => document.removeEventListener("visibilitychange", markVisible);
  }, []);

  async function login(username: string, password: string) {
    const result = await api.login(username, password);
    if (result.role !== "dispatcher") throw new Error("الحساب ليس حساب مركز خدمة");
    storeToken(result.token);
    setToken(result.token);
  }
  function flash(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3500);
  }
  async function toggleOrderSound() {
    const next = !soundEnabled;
    soundEnabledRef.current = next;
    setSoundEnabled(next);
    if (next) {
      try {
        await playIncomingOrderTone();
        flash("تم تشغيل صوت الطلبات الجديدة");
      } catch {
        soundEnabledRef.current = false;
        setSoundEnabled(false);
        flash("المتصفح منع تشغيل الصوت؛ حاول مرة أخرى بعد الضغط داخل الصفحة");
      }
    } else {
      void orderAudioContext?.suspend().catch(() => undefined);
      flash("تم إيقاف صوت الطلبات الجديدة");
    }
  }
  function acknowledgeIncomingOrders() {
    setIncomingAlert(null);
    setRecentOrderIds([]);
    setUnreadNewOrders(0);
  }
  const activeDrivers = data.staff.filter(
    (profile) => profile.role === "driver" && profile.isActive,
  );
  const allDrivers = data.staff.filter((profile) => profile.role === "driver");
  const liveOrders = useMemo(
    () => orders.filter(
      (order) => !["delivered", "cancelled"].includes(order.status),
    ),
    [orders],
  );
  const filteredOrders = useMemo(
    () => filterOperationalOrders(liveOrders, data.restaurants, data.staff, {
      query: orderQuery,
      restaurantId: orderRestaurant,
      driverId: orderDriver,
    }),
    [data.restaurants, data.staff, liveOrders, orderDriver, orderQuery, orderRestaurant],
  );
  const lateOrders = liveOrders.filter(
    (order) => getOrderAttention(order, clockNow).level === "late",
  );
  const hasOrderFilters = Boolean(orderQuery || orderRestaurant || orderDriver);
  function clearOrderFilters() {
    setOrderQuery("");
    setOrderRestaurant("");
    setOrderDriver("");
  }
  const today = new Date().toDateString();
  const deliveredToday = orders.filter(
    (order) =>
      order.status === "delivered" &&
      new Date(order.updatedAt).toDateString() === today,
  );
  if (!token) return <Login onLogin={login} />;
  if (loading)
    return (
      <main className="splash">
        <Brand />
        <span className="spinner" /> <p>جارٍ تجهيز مركز الخدمة…</p>
      </main>
    );

  return (
    <div className="app-shell">
      <aside>
        <Brand />
        <div className="nav-section-label">مساحة العمل</div>
        <nav>
          <button
            className={view === "orders" ? "active" : ""}
            onClick={() => setView("orders")}
          >
            <span className="nav-icon"><Icon name="orders" /></span>
            <span className="nav-copy"><b>الطلبات</b><small>إدارة الحركة</small></span>
          </button>
          <button
            className={view === "catalog" ? "active" : ""}
            onClick={() => setView("catalog")}
          >
            <span className="nav-icon"><Icon name="catalog" /></span>
            <span className="nav-copy"><b>الكتالوج</b><small>المطاعم والمنتجات</small></span>
          </button>
          <button
            className={view === "integrations" ? "active" : ""}
            onClick={() => setView("integrations")}
          >
            <span className="nav-icon"><Icon name="sync" /></span>
            <span className="nav-copy"><b>التكاملات</b><small>ربط المحاسبة</small></span>
          </button>
          <button
            className={view === "staff" ? "active" : ""}
            onClick={() => setView("staff")}
          >
            <span className="nav-icon"><Icon name="team" /></span>
            <span className="nav-copy"><b>الفريق</b><small>حسابات التشغيل</small></span>
          </button>
        </nav>
        <div className="aside-bottom">
          <div className="operator-card">
            <span className="operator-avatar">N</span>
            <span><b>إدارة NUVIWAY</b><small><i /> متصل الآن</small></span>
          </div>
          <button className="ghost" onClick={() => setPasswordModal(true)}>
            <Icon name="lock" size={17} /> تغيير كلمة المرور
          </button>
          <button className="logout" onClick={logout}>
            <Icon name="logout" size={17} /> تسجيل الخروج
          </button>
        </div>
      </aside>
      <main className="workspace">
        <header className="topbar">
          <div className="topbar-copy">
            <span className="eyebrow"><i /> {viewInfo[view].eyebrow}</span>
            <h1>{viewInfo[view].title}</h1>
            <p>{viewInfo[view].subtitle}</p>
          </div>
          <div className="top-actions">
            <span className="live-dot"><i /> مباشر الآن</span>
            {view === "orders" && (
              <button
                type="button"
                className={`sound-control${soundEnabled ? " enabled" : ""}`}
                aria-pressed={soundEnabled}
                onClick={() => void toggleOrderSound()}
                title={soundEnabled ? "إيقاف صوت الطلبات" : "تشغيل صوت الطلبات"}
              >
                <Icon name={soundEnabled ? "volume" : "bell"} size={17} />
                <span>{soundEnabled ? "الصوت يعمل" : "تشغيل الصوت"}</span>
              </button>
            )}
            <button className="ghost" onClick={() => void refreshAll()}>
              <Icon name="refresh" size={17} /> تحديث
            </button>
            {view === "catalog" && (
              <button
                className="primary"
                onClick={() => setRestaurantModal("new")}
              >
                <Icon name="plus" size={18} /> مطعم جديد
              </button>
            )}
            {view === "integrations" && (
              <button
                className="primary"
                onClick={() => setIntegrationModal(true)}
                disabled={data.restaurants.length === 0}
              >
                <Icon name="plus" size={18} /> ربط برنامج جديد
              </button>
            )}
            {view === "staff" && (
              <button className="primary" onClick={() => setStaffModal(true)}>
                <Icon name="plus" size={18} /> حساب تشغيل
              </button>
            )}
          </div>
        </header>
        {notice && (
          <div className="toast" role="status">
            {notice}
          </div>
        )}
        {view === "orders" && (
          <>
            <VerificationQueue requests={phoneVerifications} onChanged={refreshOrders} />
            {incomingAlert && (
              <div className="incoming-order-alert" role="alert" aria-live="assertive">
                <span className="incoming-bell"><Icon name="bell" size={25} /></span>
                <div>
                  <span className="incoming-kicker">NEW ORDER SIGNAL</span>
                  <strong>{incomingAlert.count === 1 ? "وصل طلب جديد الآن" : `وصلت ${incomingAlert.count} طلبات جديدة`}</strong>
                  <p>ظهر الطلب داخل عموده وتم وضعه في قائمة المتابعة.</p>
                </div>
                <button type="button" onClick={acknowledgeIncomingOrders}>تمت المشاهدة</button>
              </div>
            )}
            <section className="stats">
              <article>
                <div className="stat-head"><span>طلبات نشطة</span><i><Icon name="orders" /></i></div>
                <strong>{liveOrders.length}</strong>
                <small><b /> تحتاج متابعة الآن</small>
              </article>
              <article>
                <div className="stat-head"><span>بانتظار السائق</span><i><Icon name="bag" /></i></div>
                <strong>
                  {
                    orders.filter(
                      (order) => order.status === "ready_for_pickup",
                    ).length
                  }
                </strong>
                <small><b /> جاهزة للاستلام</small>
              </article>
              <article>
                <div className="stat-head"><span>على الطريق</span><i><Icon name="bike" /></i></div>
                <strong>
                  {
                    orders.filter((order) => order.status === "on_the_way")
                      .length
                  }
                </strong>
                <small><b /> قيد التوصيل</small>
              </article>
              <article>
                <div className="stat-head"><span>مبيعات اليوم</span><i><Icon name="sales" /></i></div>
                <strong>
                  {money(
                    deliveredToday.reduce((sum, order) => sum + order.total, 0),
                  )}
                </strong>
                <small><b /> {deliveredToday.length} طلب مكتمل</small>
              </article>
            </section>
            <section className="operations-toolbar" aria-label="البحث وتصفية الطلبات">
              <div className="order-search">
                <Icon name="search" size={18} />
                <input
                  ref={orderSearchRef}
                  type="search"
                  value={orderQuery}
                  onChange={(event) => setOrderQuery(event.target.value)}
                  placeholder="ابحث برقم الطلب، الهاتف، المنطقة أو المنتج…"
                  aria-label="البحث في الطلبات"
                />
                {orderQuery ? (
                  <button
                    type="button"
                    className="search-clear"
                    onClick={() => setOrderQuery("")}
                    aria-label="مسح البحث"
                  >
                    <Icon name="close" size={15} />
                  </button>
                ) : <kbd>/</kbd>}
              </div>
              <div className="order-filter">
                <Icon name="filter" size={16} />
                <select
                  value={orderRestaurant}
                  onChange={(event) => setOrderRestaurant(event.target.value)}
                  aria-label="تصفية حسب المطعم"
                >
                  <option value="">كل المطاعم</option>
                  {data.restaurants.map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                  ))}
                </select>
              </div>
              <div className="order-filter">
                <Icon name="bike" size={17} />
                <select
                  value={orderDriver}
                  onChange={(event) => setOrderDriver(event.target.value)}
                  aria-label="تصفية حسب السائق"
                >
                  <option value="">كل السائقين</option>
                  <option value="unassigned">غير مسند</option>
                  {allDrivers.map((driver) => (
                    <option key={driver.subjectId} value={driver.subjectId}>{driver.displayName}</option>
                  ))}
                </select>
              </div>
              <div className="operations-result">
                <strong>{filteredOrders.length}</strong>
                <span>من {liveOrders.length} طلب</span>
                <small>آخر تحديث {time(new Date(lastOrdersRefresh).toISOString())}</small>
              </div>
            </section>
            {lateOrders.length > 0 && (
              <div className="attention-banner" role="status">
                <span className="attention-icon"><Icon name="alert" size={20} /></span>
                <div>
                  <strong>{lateOrders.length === 1 ? "طلب واحد يحتاج تدخلاً سريعاً" : `${lateOrders.length} طلبات تحتاج تدخلاً سريعاً`}</strong>
                  <p>{hasOrderFilters ? "قد تكون بعض الطلبات خارج نتائج التصفية الحالية." : "جرى تمييزها داخل الأعمدة وترتيب الطلبات الأقدم أولاً."}</p>
                </div>
                {hasOrderFilters && (
                  <button type="button" onClick={clearOrderFilters}>إظهار جميع الطلبات</button>
                )}
              </div>
            )}
            {liveOrders.length === 0 ? (
              <div className="empty">
                <strong>لا توجد طلبات نشطة الآن</strong>
                <span>ستظهر الطلبات الجديدة هنا تلقائياً.</span>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="empty filtered-empty">
                <strong>لا توجد نتيجة مطابقة</strong>
                <span>جرّب تغيير كلمة البحث أو إزالة أحد المرشحات.</span>
                <button className="primary" type="button" onClick={clearOrderFilters}>
                  عرض كل الطلبات
                </button>
              </div>
            ) : (
              <section className="order-board">
                {(
                  [
                    "pending_merchant",
                    "preparing",
                    "ready_for_pickup",
                    "on_the_way",
                  ] as OrderStatus[]
                ).map((status) => (
                  <div className="order-column" key={status}>
                    <div className="column-title">
                      <div>
                        <strong>{statusInfo[status].label}</strong>
                        <span>{statusInfo[status].hint}</span>
                      </div>
                      <b>
                        {
                          filteredOrders.filter((order) => order.status === status)
                            .length
                        }
                      </b>
                    </div>
                    {filteredOrders
                      .filter((order) => order.status === status)
                      .map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          restaurant={data.restaurants.find(
                            (restaurant) =>
                              restaurant.id === order.restaurantId,
                          )}
                          drivers={activeDrivers}
                          driverName={allDrivers.find(
                            (driver) => driver.subjectId === order.driverId,
                          )?.displayName}
                          now={clockNow}
                          isNew={recentOrderIds.includes(order.id)}
                          onChanged={refreshOrders}
                        />
                      ))}
                  </div>
                ))}
              </section>
            )}
          </>
        )}
        {view === "catalog" && (
          <section className="catalog-list">
            {data.restaurants.length === 0 && (
              <div className="empty">
                <strong>ابدأ بإضافة أول مطعم</strong>
                <span>
                  بعد إضافة المنتجات وتفعيل المطعم سيظهر فوراً في تطبيق الزبون.
                </span>
                <button
                  className="primary"
                  onClick={() => setRestaurantModal("new")}
                >
                  إضافة أول مطعم
                </button>
              </div>
            )}
            {data.restaurants.map((restaurant) => {
              const items = data.menuItems.filter(
                (item) => item.restaurantId === restaurant.id,
              );
              return (
                <article className="restaurant-panel" key={restaurant.id}>
                  <header>
                    <div className="restaurant-symbol">{restaurant.symbol}</div>
                    <div>
                      <h2>{restaurant.name}</h2>
                      <p>
                        {restaurant.category} · {restaurant.etaMin}–
                        {restaurant.etaMax} دقيقة · توصيل{" "}
                        {money(restaurant.deliveryFee)}
                      </p>
                    </div>
                    <span
                      className={`state ${restaurant.isActive ? "on" : ""}`}
                    >
                      {restaurant.isActive ? "ظاهر" : "مخفي"}
                    </span>
                    <button
                      className="ghost"
                      onClick={() => setRestaurantModal(restaurant)}
                    >
                      تعديل
                    </button>
                    <button
                      className="secondary"
                      onClick={() =>
                        void api
                          .setRestaurantActive(
                            restaurant.id,
                            !restaurant.isActive,
                          )
                          .then(refreshAll)
                      }
                    >
                      {restaurant.isActive ? "إخفاء" : "إظهار"}
                    </button>
                  </header>
                  <div className="product-grid">
                    {items.map((item) => (
                      <div
                        className={`product-card ${item.isAvailable ? "" : "unavailable"}`}
                        key={item.id}
                      >
                        <div className="product-image">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.name} />
                          ) : (
                            <span>{restaurant.symbol}</span>
                          )}
                        </div>
                        <div>
                          <h3>{item.name}</h3>
                          <p>{item.description || "من دون وصف"}</p>
                          <strong>{money(item.price)}</strong>
                          <div className="inventory-meta">
                            {item.externalSku && <span>SKU: {item.externalSku}</span>}
                            {item.inventorySource === "accounting" && (
                              <span className={item.stockQuantity === 0 ? "empty-stock" : ""}>
                                المخزون: {item.stockQuantity ?? 0}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="product-actions">
                          <button onClick={() => setItemModal(item)}>
                            تعديل
                          </button>
                          <button
                            onClick={() =>
                              void api
                                .setItemAvailability(
                                  item.restaurantId,
                                  item.id,
                                  !item.catalogEnabled,
                                )
                                .then(refreshAll)
                            }
                          >
                            {item.catalogEnabled ? "إخفاء" : "إظهار"}
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      className="add-product"
                      onClick={() => {
                        setItemRestaurant(restaurant.id);
                        setItemModal("new");
                      }}
                    >
                      + إضافة منتج
                    </button>
                  </div>
                </article>
              );
            })}
          </section>
        )}
        {view === "integrations" && (
          <section className="integrations-layout">
            <article className="integration-guide">
              <span className="eyebrow">واجهة جاهزة لأي برنامج محاسبة</span>
              <h2>المخزون يتحدث تلقائياً من دون لمس الصور</h2>
              <p>
                يرسل برنامج المحاسبة رمز المادة والاسم والسعر والكمية. NUVIWAY
                يوقف المادة فور وصول الكمية إلى صفر، بينما تبقى الصورة والوصف
                اليدويان تحت تحكمك.
              </p>
              <ol>
                <li>أنشئ مفتاحاً مستقلاً للمطعم أو السنتر.</li>
                <li>ضع الرابط والمفتاح داخل الموصل المحاسبي.</li>
                <li>أرسل مزامنة جزئية بعد كل فاتورة أو مزامنة كاملة دورية.</li>
              </ol>
              <label>رابط المزامنة</label>
              <code dir="ltr">{inventoryEndpoint}</code>
              <details>
                <summary>مثال البيانات المطلوبة</summary>
                <pre dir="ltr">{`{
  "syncId": "invoice-2026-000123",
  "mode": "partial",
  "products": [{
    "sku": "10025",
    "name": "اسم المادة",
    "price": 25000,
    "stockQuantity": 8
  }]
}`}</pre>
              </details>
            </article>
            <div className="integration-list">
              {data.inventoryIntegrations.length === 0 ? (
                <div className="empty">
                  <strong>لا يوجد ربط محاسبي بعد</strong>
                  <span>
                    أضف المطعم أولاً، ثم أنشئ مفتاحاً آمناً لبرنامج المحاسبة.
                  </span>
                  <button
                    className="primary"
                    disabled={data.restaurants.length === 0}
                    onClick={() => setIntegrationModal(true)}
                  >
                    إنشاء أول ربط
                  </button>
                </div>
              ) : (
                data.inventoryIntegrations.map((integration) => (
                  <article
                    className={`integration-card ${integration.isActive ? "" : "inactive"}`}
                    key={integration.id}
                  >
                    <div>
                      <span
                        className={`state ${integration.isActive ? "on" : ""}`}
                      >
                        {integration.isActive ? "فعال" : "موقوف"}
                      </span>
                      <h3>{integration.name}</h3>
                      <p>
                        {data.restaurants.find(
                          (restaurant) =>
                            restaurant.id === integration.restaurantId,
                        )?.name ?? integration.restaurantId}
                      </p>
                    </div>
                    <div className="sync-time">
                      <span>آخر مزامنة</span>
                      <strong>
                        {integration.lastSyncedAt
                          ? dateTime(integration.lastSyncedAt)
                          : "لم تبدأ بعد"}
                      </strong>
                    </div>
                    {integration.isActive && (
                      <button
                        className="danger"
                        onClick={() => {
                          if (!confirm("إيقاف هذا المفتاح فوراً؟")) return;
                          void api
                            .revokeInventoryIntegration(integration.id)
                            .then(refreshAll);
                        }}
                      >
                        إيقاف المفتاح
                      </button>
                    )}
                  </article>
                ))
              )}
            </div>
          </section>
        )}
        {view === "staff" && (
          <section className="staff-grid">
            {data.staff.length === 0 && (
              <div className="empty">
                <strong>لا توجد حسابات مسجلة</strong>
                <span>أضف السائقين وحسابات المطاعم وموظفي المركز.</span>
              </div>
            )}
            {data.staff.map((profile) => (
              <article
                className={`staff-card ${profile.isActive ? "" : "inactive"}`}
                key={profile.subjectId}
              >
                <div className="avatar">{profile.displayName.slice(0, 1)}</div>
                <div>
                  <span className="role">
                    {profile.role === "driver"
                      ? "سائق"
                      : profile.role === "merchant"
                        ? "مطعم / سنتر"
                        : "مركز خدمة"}
                  </span>
                  <h3>{profile.displayName}</h3>
                  <p>{profile.phone || "لا يوجد رقم"}</p>
                  <small>
                    {profile.subjectId}
                    {profile.restaurantId
                      ? ` · ${data.restaurants.find((restaurant) => restaurant.id === profile.restaurantId)?.name ?? profile.restaurantId}`
                      : ""}
                  </small>
                </div>
                <button
                  className="secondary"
                  onClick={() =>
                    void api
                      .setStaffActive(profile.subjectId, !profile.isActive)
                      .then(refreshAll)
                  }
                >
                  {profile.isActive ? "إيقاف" : "تفعيل"}
                </button>
              </article>
            ))}
          </section>
        )}
      </main>
      {restaurantModal && (
        <RestaurantForm
          value={restaurantModal === "new" ? undefined : restaurantModal}
          onClose={() => setRestaurantModal(null)}
          onSave={async (value) => {
            await api.saveRestaurant(value);
            await refreshAll();
            flash("تم حفظ المطعم");
          }}
        />
      )}
      {itemModal && (
        <ItemForm
          restaurants={data.restaurants}
          value={itemModal === "new" ? undefined : itemModal}
          initialRestaurantId={itemRestaurant}
          onClose={() => {
            setItemModal(null);
            setItemRestaurant(undefined);
          }}
          onSave={async (value, image) => {
            let imageUrl = value.imageUrl;
            const previousImageUrl = value.imageUrl;
            let uploadedImageUrl: string | null = null;
            try {
              if (image) {
                uploadedImageUrl = (
                  await api.uploadProductImage(
                    image,
                    value.restaurantId,
                    value.id,
                  )
                ).imageUrl;
                imageUrl = uploadedImageUrl;
              }
              await api.saveMenuItem({ ...value, imageUrl });
            } catch (error) {
              if (uploadedImageUrl)
                await api.deleteProductImage(uploadedImageUrl).catch(() => undefined);
              throw error;
            }
            if (
              uploadedImageUrl &&
              previousImageUrl &&
              previousImageUrl !== uploadedImageUrl
            ) {
              await api.deleteProductImage(previousImageUrl).catch(() => undefined);
            }
            await refreshAll();
            flash("تم حفظ المنتج");
          }}
        />
      )}
      {staffModal && (
        <StaffForm
          restaurants={data.restaurants}
          onClose={() => setStaffModal(false)}
          onSave={async (value) => {
            const activation = await api.issueActivation(value);
            await refreshAll();
            return activation.code;
          }}
        />
      )}
      {integrationModal && (
        <InventoryIntegrationForm
          restaurants={data.restaurants}
          onClose={() => setIntegrationModal(false)}
          onSave={async (restaurantId, name) => {
            const result = await api.issueInventoryIntegration(
              restaurantId,
              name,
            );
            await refreshAll();
            return result;
          }}
        />
      )}
      {passwordModal && (
        <PasswordForm
          onClose={() => setPasswordModal(false)}
          onSave={async (currentPassword, newPassword) => {
            const result = await api.changePassword(currentPassword, newPassword);
            storeToken(result.token);
            setToken(result.token);
            setPasswordModal(false);
            flash("تم تغيير كلمة المرور وإلغاء الجلسات القديمة");
          }}
        />
      )}
    </div>
  );
}
